import { Typography } from '@material-ui/core';
import {
  FormikPasswordField,
  FormikTextField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import { ColorDynamic, Column, Columns, Image, Stack } from '@superdispatch/ui';
import { Alert, Box } from '@superdispatch/ui-lab';
import { useInitialValues } from 'shared/form/AppFormik';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { FormikFileDropZone } from 'shared/form/FormikFileDropZone';
import {
  ACHPaymentInfoDTO,
  ACHPaymentInfoRestrictedSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import { LightBulb } from '../assets/LightBulbIcon';
import { ShieldIcon } from '../assets/SheildIcon';
import routingAccountIllustration from '../assets/routing_account.png';
import styled from 'styled-components';
import { isWebView } from 'shared/constants/AppTypeConstants';

const DarkText = styled(Typography)`
  color: ${ColorDynamic.Dark500};
`;

const ResponsiveImage = styled(Image)`
  max-width: 100%;
`;

interface ACHChangeBankDetailsBankInfoFormProps {
  onContinue: (values: ACHPaymentInfoDTO) => void;
}

function useACHChangeBankDetailsBankInfoForm({
  onContinue,
}: ACHChangeBankDetailsBankInfoFormProps) {
  const initialValues = useInitialValues(ACHPaymentInfoRestrictedSchema);

  return useFormikEnhanced<ACHPaymentInfoDTO, ACHPaymentInfoDTO>({
    initialValues,
    validationSchema: ACHPaymentInfoRestrictedSchema,
    onSubmit: (values) => values,
    onSubmitSuccess: (values) => {
      onContinue(values);
    },
  });
}

interface ACHChangeBankDetailsBankInfoStepProps {
  onClose: () => void;
  onContinue: (values: ACHPaymentInfoDTO) => void;
}

export function ACHChangeBankDetailsBankInfoStep({
  onClose,
  onContinue,
}: ACHChangeBankDetailsBankInfoStepProps) {
  const formik = useACHChangeBankDetailsBankInfoForm({
    onContinue,
  });

  return (
    <FormikDrawerContent
      formik={formik}
      title="Change ACH Bank Details"
      onClose={onClose}
      disableAutoClose={true}
      actions={
        <FormikDrawerActions
          submitButtonLabel="Continue"
          fullWidth={isWebView}
        />
      }
    >
      <Stack space="medium">
        <Stack space="small">
          <Alert severity="info">
            <DarkText>
              SuperPay bank account isn&apos;t affected by changes here.
              Payments keep going to the bank set in SuperPay. To use the same
              bank for both, update it in SuperPay too.
            </DarkText>
          </Alert>

          <Columns space="xsmall" align="top">
            <Column width="content">
              <ShieldIcon fontSize="small" />
            </Column>
            <Column>
              <Typography color="textSecondary">
                Your data is encrypted and stored securely.
              </Typography>
            </Column>
          </Columns>

          <FormikTextField
            label="Bank Name"
            name="bank_name"
            fullWidth={true}
          />

          <FormikPasswordField
            name="bank_routing_number"
            label="Routing Number"
            helperText="A 9-digit number in the lower left corner of a check"
            fullWidth={true}
          />

          <FormikPasswordField
            name="bank_account_number"
            label="Bank Account Number"
            helperText="A 5-17 digit number next to the routing number"
            fullWidth={true}
          />
        </Stack>

        <Box padding="small" backgroundColor="Silver200" borderRadius="small">
          <Columns space="xsmall">
            <Column width="content">
              <LightBulb fontSize="small" />
            </Column>
            <Column>
              <Stack space="xsmall">
                <Typography variant="h5">
                  Where to find bank details?
                </Typography>
                <Typography color="textSecondary">
                  These numbers can be found at the bottom of a check or by
                  contacting your bank.
                </Typography>
                <ResponsiveImage
                  width="292"
                  height="106"
                  src={routingAccountIllustration}
                  alt="routing account illustration"
                />
              </Stack>
            </Column>
          </Columns>
        </Box>

        <Stack space="xsmall">
          <Typography variant="h4">Voided check</Typography>
          <Typography color="textSecondary">
            Upload a clear photo or PDF of a check with &ldquo;VOID&rdquo;
            written across it. Required to verify your bank account.
          </Typography>
          <FormikFileDropZone
            name="voided_check_url"
            isRestricted={true}
            hintText="or Drag & Drop file less than 20 MB"
          >
            <Typography color="primary">Upload Voided Check</Typography>
          </FormikFileDropZone>
        </Stack>
      </Stack>
    </FormikDrawerContent>
  );
}
